import {
  InsuranceType,
  ProductBackendName,
} from "Components/Features/CheckPrice/Products/productTypes";
import {
  CoverageTexts,
  InsuranceCoverage,
} from "Store/interfaces/products/coverage";
import { useComponentTexts, useInsuranceCoverages } from "Store/texts/hooks";
import React, { useState } from "react";
import {
  InsuranceTypeVariantVariable,
  betterInsuranceTypeFromInsuranceType,
  filterOutIrrelevantCoverage,
  getBetterInsuranceLabel,
  getInsuranceTypeCoverages,
  getInsuranceTypeExcludedCoverages,
  getOptionalInsuranceCoverages,
  insuranceTypeFromInsuranceCoverage,
} from "./coverages.helpers";
import {
  AccordionButton,
  Description,
  LeftJustifiedCollapisbleContainer,
  LeftJustifiedInsuranceTermsLink,
  LeftJustifiedIpidLinks,
} from "./coverages.styles";

import { WrapLgMarginBottom } from "App/global-style";
import { getComponentTextByFieldId, useTexts } from "Components/TextContext";
import { Data } from "Components/TextContext/textInterfaces";
import { TextLanguage } from "Store/texts/interfaces";
import { buttonTrackId } from "Utils/analytics";
import { isDefined } from "Utils/functions";
import { CoverageCardAccordion } from "./CoverageCardAccordion";

interface Props {
  readonly product: ProductBackendName;
  readonly cardIsSelected?: boolean;
  readonly coverage: InsuranceCoverage;
  readonly showExcludedCoverages?: boolean;
  readonly textComponents?: Data[];
  readonly doNotShow?: InsuranceType;
  readonly overriddenBetterInsuranceText?: string;
  readonly overriddenIncludedCoverageTexts?: CoverageTexts[];
  readonly overriddenExcludedCoverageTexts?: CoverageTexts[];
  readonly insuranceTypeVariant?: InsuranceTypeVariantVariable;
}

export const CoverageCardDetails = ({
  product,
  cardIsSelected,
  coverage,
  showExcludedCoverages,
  textComponents,
  doNotShow,
  overriddenBetterInsuranceText,
  overriddenExcludedCoverageTexts,
  overriddenIncludedCoverageTexts,
  insuranceTypeVariant,
}: Props): React.ReactElement => {
  const [{ coverageCard }, lang] = useComponentTexts();
  const [showDetails, toggleDetails] = useState(false);
  const insuranceType = insuranceTypeFromInsuranceCoverage(
    coverage,
    insuranceTypeVariant,
  );
  const coverageTexts = filterOutIrrelevantCoverage(
    useInsuranceCoverages(product),
    insuranceType,
    doNotShow,
  );
  const { getText } = useTexts();
  const betterInsuranceType =
    betterInsuranceTypeFromInsuranceType(insuranceType);

  const includedCoverages =
    overriddenIncludedCoverageTexts ||
    getInsuranceTypeCoverages(coverageTexts, insuranceType);

  const excludedCoverages =
    overriddenExcludedCoverageTexts ||
    getInsuranceTypeExcludedCoverages(
      coverageTexts,
      insuranceType,
      betterInsuranceType,
    );

  const optionalCoverages = getOptionalInsuranceCoverages(
    coverageTexts,
    insuranceType,
  );

  const insuranceDisplayName = isDefined(textComponents)
    ? getComponentTextByFieldId(
        textComponents,
        `coveragelabel-${coverage.toLowerCase()}`,
      )
    : undefined;

  const betterInsuranceText = (): string | undefined => {
    if (overriddenBetterInsuranceText) {
      return overriddenBetterInsuranceText;
    }
    if (getBetterInsuranceLabel(coverage) !== "") {
      return getText("coveragecard.subheaders.abettercoveragecovers", {
        variables: { coverageCard_coverage: getBetterInsuranceLabel(coverage) },
      });
    }
    return undefined;
  };

  return (
    <>
      <AccordionButton
        className="coverage-card__show-details-button"
        variant="text"
        iconRight={showDetails ? "chevron-up" : "chevron-down"}
        data-trackid={buttonTrackId(coverageCard.info[TextLanguage.ENGLISH])}
        onClick={() => toggleDetails(!showDetails)}
      >
        <p>{getText("coveragecard.buttons.seecoverage")}</p>
      </AccordionButton>
      <LeftJustifiedCollapisbleContainer isOpen={showDetails}>
        <Description fw={700} size={"16px"}>
          {insuranceDisplayName
            ? `${insuranceDisplayName} ${coverageCard.covers[lang]}`
            : coverageCard.coverage[lang]}
        </Description>
        <WrapLgMarginBottom>
          <CoverageCardAccordion
            cardId={String(coverage)}
            coverages={includedCoverages}
            cardIsSelected={cardIsSelected}
            includedCoverages
            lang={lang}
          />
        </WrapLgMarginBottom>
        {optionalCoverages.length > 0 && (
          <>
            <Description fw={700} size={"16px"}>
              {getText("coveragecard.subheaders.optionalcoverages")}
            </Description>
            <WrapLgMarginBottom>
              <CoverageCardAccordion
                cardId={String(coverage)}
                coverages={optionalCoverages}
                cardIsSelected={cardIsSelected}
                lang={lang}
              />
            </WrapLgMarginBottom>
          </>
        )}
        {showExcludedCoverages && excludedCoverages.length > 0 && (
          <>
            <Description fw={700} size={"16px"}>
              {betterInsuranceText()}
            </Description>
            <CoverageCardAccordion
              cardId={String(coverage)}
              coverages={excludedCoverages}
              cardIsSelected={cardIsSelected}
              lang={lang}
            />
          </>
        )}
        <LeftJustifiedIpidLinks
          coverage={coverage}
          isOnColoredBackground={cardIsSelected}
        />
        <LeftJustifiedInsuranceTermsLink
          productBackendName={product}
          isOnColoredBackground={cardIsSelected}
        />
      </LeftJustifiedCollapisbleContainer>
    </>
  );
};
